import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, ArrowRight, Zap, Shield, RefreshCw, Plug, Sparkles } from 'lucide-react';

const integrations = [
  {
    name: 'HubSpot',
    status: 'live' as const,
    logo: '/logos/hubspot.svg',
    tagline: 'Deep bi-directional CRM sync',
    description: 'Full bi-directional sync with HubSpot CRM v3 API. Supports contacts, companies, deals, tickets, and all custom objects. Works alongside HubSpot Breeze and existing workflows.',
    features: [
      'Bi-directional contact & deal sync',
      'Custom object & field mapping',
      'Workflow-compatible (triggers fire normally)',
      'Properties API auto-discovery',
      'Association labels support',
    ],
    auth: 'OAuth2 via app.hubspot.com',
  },
  {
    name: 'Google Workspace',
    status: 'live' as const,
    logo: '/logos/google-workspace.svg',
    tagline: 'Calendar, Meet & Gmail intelligence',
    description: 'Connects to Google Calendar for meeting context, Google Meet for transcript ingestion, and Gmail for thread analysis. All used to enrich deal intelligence.',
    features: [
      'Google Calendar event sync',
      'Google Meet transcript ingestion',
      'Gmail thread analysis for deal signals',
      'Contact enrichment from email metadata',
      'Meeting prep with attendee context',
    ],
    auth: 'Google OAuth2',
  },
  {
    name: 'Slack',
    status: 'live' as const,
    logo: '/logos/slack.svg',
    tagline: 'Real-time deal alerts & coaching',
    description: 'Push deal alerts, coaching nudges, pipeline summaries, and action items directly to your Slack channels. No context-switching needed.',
    features: [
      'Deal stage change notifications',
      'AI coaching nudges in-channel',
      'Pipeline summary reports',
      'Action item reminders',
      'Custom channel routing',
    ],
    auth: 'Slack App OAuth2',
  },
  {
    name: 'Microsoft 365',
    status: 'beta' as const,
    logo: '/logos/microsoft-365.svg',
    tagline: 'Teams, Outlook & Calendar — private beta',
    description: 'Currently in private beta with selected enterprise customers. Integrates with Microsoft Teams meetings for transcript capture, Outlook for email intelligence, and Calendar for scheduling context. Contact us to join the beta.',
    features: [
      'Teams meeting transcript ingestion',
      'Outlook email thread analysis',
      'Calendar sync for meeting context',
      'Contact enrichment from Microsoft Graph',
      'SharePoint document references',
    ],
    auth: 'Microsoft Identity Platform OAuth2',
  },
  {
    name: 'Zoom',
    status: 'live' as const,
    logo: '/logos/zoom.svg',
    tagline: 'Meeting recordings & deal signals',
    description: 'Automatically ingests Zoom meeting recordings and transcripts. Extracts deal signals, competitor mentions, and next steps from conversations.',
    features: [
      'Automatic recording ingestion',
      'Transcript-based deal signal extraction',
      'Competitor mention detection',
      'Next-step identification',
      'Meeting outcome classification',
    ],
    auth: 'Zoom OAuth2',
  },
  {
    name: 'Salesforce',
    status: 'live' as const,
    logo: '/logos/salesforce.svg',
    tagline: 'Full CRM sync with custom objects',
    description: 'Deep Salesforce integration via REST API v59.0. Supports all standard and custom objects (__c), Flows, Process Builder, and Apex triggers. Official Salesforce Partner.',
    features: [
      'Full REST API v59.0 integration',
      'All standard objects (Lead, Contact, Opportunity…)',
      'Custom objects (__c) auto-discovery',
      'Compatible with Flows & Process Builder',
      'Works alongside Einstein/Agentforce',
    ],
    auth: 'Salesforce OAuth2 (login.salesforce.com)',
  },
  {
    name: 'Pipedrive',
    status: 'live' as const,
    logo: '/logos/pipedrive.svg',
    tagline: 'Pipeline sync & activity logging',
    description: 'Sync your Pipedrive pipeline, log activities automatically from voice, and manage deals without leaving your workflow.',
    features: [
      'Pipeline & deal sync',
      'Voice-powered activity logging',
      'Contact & organization management',
      'Custom field support',
      'Webhook-based real-time updates',
    ],
    auth: 'Pipedrive OAuth2',
  },
  {
    name: 'LinkedIn Sales Navigator',
    status: 'coming' as const,
    logo: '/logos/linkedin.svg',
    tagline: 'Contact enrichment & relationship intel',
    description: 'Enrich your CRM contacts with LinkedIn data. Track relationship strength, identify warm introductions, and surface buying signals from LinkedIn activity.',
    features: [
      'Contact data enrichment',
      'Relationship intelligence scoring',
      'Warm introduction identification',
      'Buying signal detection',
      'Company news & trigger events',
    ],
    auth: 'LinkedIn OAuth2',
  },
];

const Integrations = () => {
  const liveIntegrations = integrations.filter(i => i.status === 'live');
  const betaIntegrations = integrations.filter(i => i.status === 'beta');
  const comingIntegrations = integrations.filter(i => i.status === 'coming');

  return (
    <Layout>
      <SEOHead
        title="NoQ5 Integrations | HubSpot, Salesforce, Slack, Zoom"
        description="NoQ5.ai integrates with HubSpot, Google Workspace, Slack, Microsoft 365, Zoom and more. See all live and upcoming integrations for your sales stack."
        canonicalUrl="https://noq5.ai/integrations"
        keywords="NoQ5 integrations, HubSpot integration, Salesforce integration, Slack CRM alerts, Zoom meeting transcripts, CRM automation integrations"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "NoQ5.ai Integrations",
          "description": "All integrations available with NoQ5.ai revenue intelligence platform",
          "url": "https://noq5.ai/integrations"
        }]}
      />

      <div className="container px-4 md:px-6 py-12 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-16"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary mb-4"
            >
              <Plug className="w-4 h-4" />
              {liveIntegrations.length} Live · {betaIntegrations.length} Beta · {comingIntegrations.length} Roadmap
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Plugs into your <span className="text-primary">existing stack</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              NoQ5 connects to the tools your team already uses. Every integration is clearly labelled Live, Beta or Roadmap so you know exactly what you're getting. All use OAuth2 with EU data residency.
            </p>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { icon: Shield, label: 'OAuth2 Secure' },
              { icon: RefreshCw, label: 'Auto Token Refresh' },
              { icon: Zap, label: '<500ms Write Latency' },
              { icon: CheckCircle2, label: 'GDPR Compliant' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 justify-center p-3 bg-muted/50 rounded-sm text-sm text-muted-foreground">
                <item.icon className="w-4 h-4 text-primary" />
                {item.label}
              </div>
            ))}
          </div>

          {/* Live Integrations */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-primary animate-pulse" />
              Live Integrations
            </h2>
            <div className="space-y-6">
              {liveIntegrations.map((integration, index) => (
                <motion.div
                  key={integration.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-sm border border-border p-6 md:p-8 hover:border-primary/30 transition-all"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <img src={integration.logo} alt={integration.name} className="w-8 h-8 object-contain" />
                        <h3 className="text-xl font-bold text-foreground">{integration.name}</h3>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" />
                          Live
                        </span>
                      </div>
                      <p className="text-sm font-medium text-primary mb-2">{integration.tagline}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4">{integration.description}</p>
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">Auth:</strong> {integration.auth}
                      </p>
                    </div>
                    <div className="md:w-72 flex-shrink-0">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Key Features</h4>
                      <ul className="space-y-2">
                        {integration.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Beta */}
          {betaIntegrations.length > 0 && (
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-primary" />
                In Beta
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl">
                Currently rolling out to selected customers. Production-grade but not yet GA — please contact us if you need it for your team.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {betaIntegrations.map((integration, index) => (
                  <motion.div
                    key={integration.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="bg-card rounded-sm border border-border p-6"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <img src={integration.logo} alt={integration.name} className="w-7 h-7 object-contain" />
                      <h3 className="text-lg font-bold text-foreground">{integration.name}</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-xs font-medium">Beta</span>
                    </div>
                    <p className="text-sm font-medium text-primary mb-2">{integration.tagline}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{integration.description}</p>
                    <ul className="space-y-1.5">
                      {integration.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Roadmap */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
              <Clock className="w-6 h-6 text-muted-foreground" />
              On the Roadmap
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {comingIntegrations.map((integration, index) => (
                <motion.div
                  key={integration.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-sm border border-border p-6 opacity-80"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <img src={integration.logo} alt={integration.name} className="w-7 h-7 object-contain" />
                    <h3 className="text-lg font-bold text-foreground">{integration.name}</h3>
                  </div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">{integration.tagline}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{integration.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* DPIA & Compliance */}
          <div className="bg-gradient-to-br from-primary/5 via-card to-primary/5 rounded-sm p-8 border border-primary/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Integration Security & DPIA</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Data Protection Impact Assessments</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  NoQ5.ai maintains Data Protection Impact Assessments (DPIAs) for all integrations involving personal data processing. These are available upon request for enterprise customers and regulatory compliance reviews.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />DPIA conducted for each CRM integration</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />PII anonymized before LLM processing</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />Data Processing Agreements (DPAs) available</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />Regular compliance audits & reviews</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Integration Security Model</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every integration follows our zero-trust security model. OAuth2 tokens are encrypted at rest with AES-256-GCM, automatically refreshed, and stored in isolated tenant partitions.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />AES-256-GCM token encryption at rest</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />Automatic token refresh with zero downtime</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />Multi-tenant row-level isolation</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />EU data residency (Ireland, AWS EU-West-1)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary rounded-sm p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">
              Need an integration we don't have?
            </h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Tell us what you need. We ship fast. Or start your free trial and connect in under 5 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-sm"
                asChild
              >
                <a href="/?scrollTo=demo">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button
                size="lg"
                className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-sm border-2 border-background"
                asChild
              >
                <a href="https://app.noq5.ai/" target="_blank" rel="noopener noreferrer">
                  Book a Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Integrations;
